import type { ExamQuestionAnswerPayloadEntry } from '../pages/examportal/utils/examAnswerPayload';

const STORAGE_PREFIX = 'jsyog_offline_answers_';

export type OfflineAnswerMap = Record<
  string,
  {
    entry: ExamQuestionAnswerPayloadEntry;
    timestamp: number;
  }
>;

const AnswerOfflineStorageService = {
  getPendingAnswers(sessionId: number): OfflineAnswerMap {
    try {
      const key = `${STORAGE_PREFIX}${sessionId}`;
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      return JSON.parse(raw) as OfflineAnswerMap;
    } catch (err) {
      console.error('Failed to read offline answers from localStorage', err);
      return {};
    }
  },

  saveAnswer(sessionId: number, questionId: string, entry: ExamQuestionAnswerPayloadEntry): void {
    try {
      const key = `${STORAGE_PREFIX}${sessionId}`;
      const current = this.getPendingAnswers(sessionId);
      current[questionId] = {
        entry,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(current));
    } catch (err) {
      console.error('Failed to save offline answer to localStorage', err);
    }
  },

  saveAnswers(sessionId: number, answers: OfflineAnswerMap): void {
    try {
      const key = `${STORAGE_PREFIX}${sessionId}`;
      localStorage.setItem(key, JSON.stringify(answers));
    } catch (err) {
      console.error('Failed to save offline answers to localStorage', err);
    }
  },

  clearPendingAnswers(sessionId: number, questionIds?: string[]): void {
    try {
      const key = `${STORAGE_PREFIX}${sessionId}`;
      if (!questionIds) {
        localStorage.removeItem(key);
        return;
      }

      const current = this.getPendingAnswers(sessionId);
      let changed = false;
      for (const qid of questionIds) {
        if (current[qid]) {
          delete current[qid];
          changed = true;
        }
      }

      if (changed) {
        if (Object.keys(current).length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(current));
        }
      }
    } catch (err) {
      console.error('Failed to clear offline answers from localStorage', err);
    }
  },

  hasPendingAnswers(sessionId: number): boolean {
    const pending = this.getPendingAnswers(sessionId);
    return Object.keys(pending).length > 0;
  },
};

export default AnswerOfflineStorageService;
